/**
 * §v3.light-mode (2026-05-01) → §v5.two-aurora (2026-06-23) — the sole theme
 * control: a small icon button in the AppShell rail's profile card that flips
 * between the two Aurora variants (aurora-light ↔ aurora-dusk), presented as
 * light / dark mode. The floating multi-theme switcher pill was removed.
 *
 * Listens to the `xpand:theme-change` CustomEvent that `theme.ts` dispatches,
 * so the icon stays in sync with the persisted value + programmatic changes
 * without prop-drilling.
 */

import { useEffect, useState } from "react";
import { Moon, Sun } from "lucide-react";

import { cn } from "@/lib/cn";
import { getTheme, toggleTheme, type Theme } from "@/lib/theme";

export function ThemeToggle({ className }: { className?: string }) {
  const [theme, setLocalTheme] = useState<Theme>(getTheme());

  useEffect(() => {
    // Sync to other instances + the pre-paint bootstrap value.
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<Theme>).detail;
      if (detail === "aurora-light" || detail === "aurora-dusk") {
        setLocalTheme(detail);
      }
    };
    window.addEventListener("xpand:theme-change", handler);
    return () => window.removeEventListener("xpand:theme-change", handler);
  }, []);

  const isDark = theme === "aurora-dusk";
  // Tooltip describes what the click WILL do, not the current state —
  // standard pattern (Apple, GitHub, Linear).
  const next = isDark ? "light" : "dark";
  const Icon = isDark ? Sun : Moon;

  return (
    <button
      type="button"
      aria-label={`Switch to ${next} mode`}
      title={`Switch to ${next} mode`}
      onClick={() => toggleTheme()}
      className={cn(
        "grid h-7 w-7 place-items-center rounded-g-sm text-fg-3",
        "transition-colors duration-g-fast ease-stand-glass",
        "hover:bg-glass-3 hover:text-fg-1",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40",
        className,
      )}
    >
      <Icon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
    </button>
  );
}
